import * as React from "react"
import { format, subDays, startOfDay } from "date-fns"
import { es } from "date-fns/locale"
import { CalendarIcon } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Calendar } from "@/components/ui/calendar"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { cn } from "@/lib/utils"

interface DatePickerProps {
    value: string                   // "YYYY-MM-DD" o ""
    onChange: (date: string) => void
    placeholder?: string
    disabled?: boolean
    disableFuture?: boolean         // bloquea fechas posteriores a hoy
    disablePast?: boolean           // bloquea fechas anteriores a hoy
    maxDaysBack?: number            // máx días hacia atrás permitidos desde hoy
    className?: string
}

export function DatePicker({
    value,
    onChange,
    placeholder = "Seleccionar fecha...",
    disabled = false,
    disableFuture = false,
    disablePast = false,
    maxDaysBack,
    className,
}: DatePickerProps) {
    const [open, setOpen] = React.useState(false)

    // T12:00:00 evita desfase de zona horaria al parsear "YYYY-MM-DD"
    const selected = value ? new Date(`${value}T12:00:00`) : undefined
    const today = startOfDay(new Date())

    const isDisabled = (date: Date) => {
        const d = startOfDay(date)
        if (disableFuture && d > today) return true
        if (disablePast && d < today) return true
        if (maxDaysBack !== undefined && d < subDays(today, maxDaysBack)) return true
        return false
    }

    const handleSelect = (date: Date | undefined) => {
        if (!date) return
        onChange(format(date, "yyyy-MM-dd"))
        setOpen(false)
    }

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <Button
                    type="button"
                    variant="outline"
                    disabled={disabled}
                    className={cn(
                        "w-full justify-start text-left font-normal",
                        !selected && "text-muted-foreground",
                        className
                    )}
                >
                    <CalendarIcon className="mr-2 h-4 w-4 shrink-0 text-muted-foreground" />
                    {selected
                        ? format(selected, "dd MMM yyyy", { locale: es })
                        : <span>{placeholder}</span>}
                </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                    mode="single"
                    selected={selected}
                    onSelect={handleSelect}
                    defaultMonth={selected ?? today}
                    disabled={isDisabled}
                    captionLayout="dropdown"
                />
            </PopoverContent>
        </Popover>
    )
}
